import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import { toast } from "sonner";
import { Loader2, ChevronDown, ChevronUp, Clock, ArrowLeft, Rocket } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Shared starfield (same as LandingPage) ───────────────────────────────────
function Starfield() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    let animId;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const stars = Array.from({ length: 280 }, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      r: Math.random() * 1.5 + 0.2, alpha: Math.random() * 0.7 + 0.3,
      speed: Math.random() * 0.2 + 0.04,
      twinkle: Math.random() * Math.PI * 2, twinkleSpeed: Math.random() * 0.018 + 0.004,
    }));
    const shoots = [];
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const g = ctx.createRadialGradient(canvas.width*.4, canvas.height*.35, 0, canvas.width*.4, canvas.height*.35, canvas.width*.9);
      g.addColorStop(0, "rgba(8,12,35,1)"); g.addColorStop(.5, "rgba(4,6,20,1)"); g.addColorStop(1, "rgba(2,3,12,1)");
      ctx.fillStyle = g; ctx.fillRect(0, 0, canvas.width, canvas.height);
      const neb = ctx.createRadialGradient(canvas.width*.75, canvas.height*.2, 0, canvas.width*.75, canvas.height*.2, canvas.width*.45);
      neb.addColorStop(0, "rgba(0,80,200,0.07)"); neb.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = neb; ctx.fillRect(0, 0, canvas.width, canvas.height);
      const neb2 = ctx.createRadialGradient(canvas.width*.2, canvas.height*.75, 0, canvas.width*.2, canvas.height*.75, canvas.width*.3);
      neb2.addColorStop(0, "rgba(100,0,180,0.05)"); neb2.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = neb2; ctx.fillRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.twinkle += s.twinkleSpeed; s.y -= s.speed * 0.08;
        if (s.y < 0) { s.y = canvas.height; s.x = Math.random() * canvas.width; }
        const tw = Math.sin(s.twinkle) * 0.3 + 0.7;
        ctx.save(); ctx.globalAlpha = s.alpha * tw; ctx.shadowBlur = s.r > 1.1 ? 6 : 2;
        ctx.shadowColor = "rgba(150,180,255,0.8)"; ctx.fillStyle = `rgba(220,230,255,${s.alpha * tw})`;
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2); ctx.fill(); ctx.restore();
      });
      if (shoots.length < 2 && Math.random() < 0.006) {
        shoots.push({ x: Math.random() * canvas.width * .6, y: Math.random() * canvas.height * .35, len: Math.random() * 100 + 50, speed: Math.random() * 7 + 4, alpha: 1, angle: Math.PI / 5 + (Math.random() - .5) * .3 });
      }
      for (let i = shoots.length - 1; i >= 0; i--) {
        const sh = shoots[i];
        const gr = ctx.createLinearGradient(sh.x, sh.y, sh.x + Math.cos(sh.angle)*sh.len, sh.y + Math.sin(sh.angle)*sh.len);
        gr.addColorStop(0, `rgba(255,255,255,${sh.alpha})`); gr.addColorStop(1, "rgba(255,255,255,0)");
        ctx.save(); ctx.strokeStyle = gr; ctx.lineWidth = 1.5; ctx.globalAlpha = sh.alpha;
        ctx.beginPath(); ctx.moveTo(sh.x, sh.y); ctx.lineTo(sh.x + Math.cos(sh.angle)*sh.len, sh.y + Math.sin(sh.angle)*sh.len);
        ctx.stroke(); ctx.restore();
        sh.x += Math.cos(sh.angle)*sh.speed; sh.y += Math.sin(sh.angle)*sh.speed;
        sh.alpha -= 0.02; if (sh.alpha <= 0) shoots.splice(i, 1);
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} className="fixed inset-0 w-full h-full" style={{ zIndex: 0 }} />;
}

// ─── Floating orb decorations ─────────────────────────────────────────────────
function Orb({ size, top, left, color, blur, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.5, delay, ease: "easeOut" }}
      className="absolute rounded-full pointer-events-none"
      style={{ width: size, height: size, top, left, background: color, filter: `blur(${blur}px)`, zIndex: 1 }}
    />
  );
}

// ─── Main Login Page ──────────────────────────────────────────────────────────
export default function LoginPage() {
  const [msLoading, setMsLoading]       = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [loginData, setLoginData]       = useState({ email: "", password: "" });

  const { login, loginWithToken }   = useAuth();
  const navigate    = useNavigate();
  const initRef     = useRef(false);

  const processHash = useCallback(async (hash) => {
    const params    = new URLSearchParams(hash.replace(/^#/, ""));
    const idToken   = params.get("id_token");
    const accToken  = params.get("access_token");
    const errorDesc = params.get("error_description");
    const error     = params.get("error");
    if (error) { toast.error(errorDesc || error); setMsLoading(false); return; }
    if (!idToken) { toast.error("No se recibió token de Microsoft"); setMsLoading(false); return; }
    try {
      // Usar window.location.origin garantiza que se llame al backend correcto
      // tanto en preview como en producción. Axios evita el error "body stream already read"
      // causado por interceptores de fetch en el entorno de preview.
      const backendBase = window.location.origin;
      const { data } = await axios.post(
        `${backendBase}/api/auth/microsoft/login`,
        { id_token: idToken, access_token: accToken }
      );
      // Actualizar AuthContext (token + user) para que ProtectedRoute funcione
      await loginWithToken(data.access_token, data.user);
      // Usar window.location.replace en lugar de navigate() para evitar la race condition
      // donde ProtectedRoute lee user=null antes de que React procese el batch de setState.
      // La recarga garantiza que initAuth() encuentre el token en localStorage.
      window.location.replace('/launch');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Error al iniciar sesión con Microsoft";
      if (msg.includes("pendiente")) { setPendingApproval(true); toast.info("Cuenta pendiente de aprobación"); }
      else toast.error(msg);
      setMsLoading(false);
    }
  }, [navigate, loginWithToken]);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    const hash     = window.location.hash;
    const captured = sessionStorage.getItem("msal_auth_hash");
    const toProcess = hash || captured;
    if (toProcess && (toProcess.includes("id_token=") || toProcess.includes("error="))) {
      sessionStorage.removeItem("msal_auth_hash");
      if (window.history.replaceState) window.history.replaceState(null, "", window.location.pathname);
      setMsLoading(true);
      processHash(toProcess);
    }
  }, [processHash]);

  const handleMicrosoftLogin = () => {
    const clientId = process.env.REACT_APP_AZURE_CLIENT_ID;
    const tenantId = process.env.REACT_APP_AZURE_TENANT_ID;
    if (!clientId || !tenantId) { toast.error("SSO no configurado"); return; }
    setMsLoading(true);
    const state = btoa(JSON.stringify({ ts: Date.now() }));
    const nonce = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("msal_auth_state", state);
    const params = new URLSearchParams({
      client_id: clientId, response_type: "id_token token",
      redirect_uri: window.location.origin, scope: "openid profile User.Read",
      response_mode: "fragment", state, nonce, prompt: "select_account",
    });
    window.location.href = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setPendingApproval(false);
    try {
      await login(loginData.email, loginData.password);
      navigate("/launch", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || "Email o contraseña incorrectos";
      if (err.response?.status === 403 && detail.includes("pendiente")) setPendingApproval(true);
      else toast.error(detail);
    } finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ background: "#020409" }}>
      <Starfield />

      {/* Ambient orbs */}
      <Orb size={400} top="-100px" left="-100px"  color="rgba(29,78,216,0.12)"  blur={80} delay={0.2} />
      <Orb size={350} top="60%"    left="70%"      color="rgba(124,58,237,0.1)"  blur={90} delay={0.5} />
      <Orb size={250} top="20%"    left="65%"      color="rgba(0,200,150,0.06)"  blur={70} delay={0.8} />

      {/* Back to landing */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3 }}
        onClick={() => navigate("/bienvenida")}
        className="absolute top-5 left-5 z-20 flex items-center gap-2 text-white/40 hover:text-white/80 text-xs transition-colors"
        data-testid="back-to-landing"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Inicio
      </motion.button>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.25, 0.4, 0.25, 1] }}
        className="relative z-10 w-full max-w-sm mx-4"
      >
        {/* Glow behind card */}
        <div className="absolute inset-0 rounded-3xl blur-2xl opacity-30"
          style={{ background: "linear-gradient(135deg, rgba(29,78,216,0.5), rgba(124,58,237,0.4))", transform: "scale(1.08)" }} />

        <div
          className="relative rounded-3xl p-8 space-y-6"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            boxShadow: "0 32px 64px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
        >
          {/* Logo + title */}
          <div className="text-center space-y-3">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
              className="mx-auto flex items-center justify-center py-2"
            >
              <img src="/valoriza-logo.svg" alt="Valoriza" className="h-9 w-auto object-contain" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
              <h1 className="text-lg font-bold text-white tracking-tight">Innovation OS</h1>
              <p className="text-white/35 text-xs mt-0.5">Plataforma de Innovación</p>
            </motion.div>
          </div>

          {/* Pending approval alert */}
          <AnimatePresence>
            {pendingApproval && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                <Alert className="border-amber-500/30 bg-amber-500/10" data-testid="pending-approval-alert">
                  <Clock className="h-3.5 w-3.5 text-amber-400" />
                  <AlertDescription className="text-amber-300 text-xs">
                    Cuenta <strong>pendiente de aprobación</strong> — recibirás un email.
                  </AlertDescription>
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {/* PRIMARY: Microsoft SSO */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="space-y-2"
          >
            <p className="text-center text-white/25 text-[10px] uppercase tracking-widest font-medium">
              Acceso corporativo
            </p>
            <motion.button
              whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(29,78,216,0.5)" }}
              whileTap={{ scale: 0.98 }}
              onClick={handleMicrosoftLogin}
              disabled={msLoading}
              data-testid="microsoft-login-btn"
              className="w-full h-12 rounded-2xl flex items-center justify-center gap-3 text-sm font-semibold text-white relative overflow-hidden"
              style={{ background: "linear-gradient(135deg, #1d4ed8 0%, #7c3aed 100%)" }}
            >
              {/* Shimmer */}
              <div className="absolute inset-0 rounded-2xl"
                style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 60%)" }} />
              {msLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin relative z-10" /><span className="relative z-10">Conectando...</span></>
              ) : (
                <>
                  <svg className="w-4 h-4 flex-shrink-0 relative z-10" viewBox="0 0 21 21">
                    <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
                    <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
                    <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                  </svg>
                  <span className="relative z-10">Acceder con Microsoft</span>
                  <Rocket className="w-3.5 h-3.5 relative z-10 opacity-70" />
                </>
              )}
            </motion.button>
          </motion.div>

          {/* Divider */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.75 }}
            className="relative flex items-center justify-center"
          >
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <button
              type="button"
              onClick={() => setShowEmailForm(!showEmailForm)}
              className="relative flex items-center gap-1.5 px-4 py-1.5 text-[11px] text-white/35 hover:text-white/65 transition-colors rounded-full"
              style={{ background: "rgba(10, 18, 40, 0.95)" }}
              data-testid="toggle-email-form-btn"
            >
              {showEmailForm ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              Acceder con usuario y contraseña
            </button>
          </motion.div>

          {/* SECONDARY: Email / password */}
          <AnimatePresence>
            {showEmailForm && (
              <motion.form
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                onSubmit={handleLogin}
                className="space-y-3 overflow-hidden"
                data-testid="email-login-form"
              >
                <div className="space-y-1.5">
                  <Label htmlFor="login-email" className="text-white/50 text-xs">Email</Label>
                  <Input
                    id="login-email" type="email"
                    placeholder="tu@valorizasm.com"
                    value={loginData.email}
                    onChange={e => setLoginData({ ...loginData, email: e.target.value })}
                    required autoComplete="email"
                    data-testid="login-email"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-blue-500/50 focus:ring-blue-500/20 rounded-xl"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="login-password" className="text-white/50 text-xs">Contraseña</Label>
                  <Input
                    id="login-password" type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={e => setLoginData({ ...loginData, password: e.target.value })}
                    required autoComplete="current-password"
                    data-testid="login-password"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-blue-500/50 focus:ring-blue-500/20 rounded-xl"
                  />
                </div>
                <Button
                  type="submit" disabled={loading}
                  data-testid="login-submit"
                  className="w-full rounded-xl bg-white/8 border border-white/10 text-white/80 hover:bg-white/12 hover:text-white"
                  variant="ghost"
                >
                  {loading && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
                  Iniciar sesión
                </Button>
              </motion.form>
            )}
          </AnimatePresence>

          {/* Footer */}
          <p className="text-center text-white/15 text-[10px]">
            © 2025 Valoriza Servicios Medioambientales
          </p>
        </div>
      </motion.div>
    </div>
  );
}
